/* eslint-disable react/require-default-props */
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { playerSelector as stateSelector } from 'reduxModules/player/selectors'
import { playerDispatchers as dispatchers } from 'reduxModules/player/dispatchers'
import { withRouter } from 'react-router'
import Collapsible from 'react-collapsible'

import {
  TutorialPropTypes,
  TutorialDefaultProps
} from 'components/Tutorials/TutorialDetail'
import _ from 'lodash'

import './styles.css'
import RelevantToggle from '../../../NewVideoPlayer/PlayerSidebar/RelevantToggle'

import convertTime, { toSeconds } from '../../../utils/convertTime'

import { getSecondsFromTimestamp } from '../../../NewVideoPlayer/utils'

const MAX_CHAPTERS_TO_SHOW_BY_DEFAULT = 11

const removeAfterDash = (str) => str.replace(/\s*-.*/, '').trim()
const formatDuration = (seconds) =>
  seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)}min`

function TutorialCurriculum({
  playerId,
  setCurrentChapter,
  chapters,
  router,
  slug,
  relevantChapters,
  shouldOnlyShowRelevants,
  location,
  toggleShowingOnlyRelevantChapters
}) {
  const getPlayer = () => window.jwplayer(playerId || 'jwplayer-0')
  const playChapter = (i, id, position) => {
    const chapterIndex = Number(i) - 1
    const player =
      typeof window.jwplayer === 'function' ? getPlayer() : undefined

    // If player is active set chapter
    if (player && typeof player.getPlaylistIndex === 'function') {
      if (chapterIndex === -1) player.playlistItem(0)
      player.once('play', () => player.seek(toSeconds(position)))
      player.playlistItem(chapterIndex)
      setCurrentChapter(id, chapterIndex)
    }
    // Update route while preserving previous query params
    const searchParams = new URLSearchParams(location?.query)
    searchParams.set('watch', 1)
    searchParams.set('chapter', i)
    router.push(`/tutorials/${slug}?${searchParams.toString()}`)
    window.scrollTo(0, 0)
  }
  const chaptersToDisplay =
    shouldOnlyShowRelevants && relevantChapters.length > 0
      ? relevantChapters
      : chapters
  const isChapterNowPlaying = (chapterIndex) => {
    const player =
      typeof window.jwplayer === 'function' ? getPlayer() : undefined
    const currentlyPlaying =
      typeof player?.getPlaylistIndex === 'function'
        ? player?.getPlaylistIndex()
        : undefined
    return chapterIndex === currentlyPlaying
  }
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }
  const shouldShowMore =
    chaptersToDisplay?.length > MAX_CHAPTERS_TO_SHOW_BY_DEFAULT

  const trigger = (index, chapter) => (
    <div className="trigger">
      {`${index + 1}. ${removeAfterDash(chapter.title)} - ${formatDuration(
        getSecondsFromTimestamp({
          durationH: chapter?.durationH,
          durationM: chapter?.durationM,
          durationS: chapter?.durationS
        })
      )}`}{' '}
      <button
        className="play-chapter"
        type="button"
        onClick={(e) => {
          playChapter(index + 1, chapter?.id, 0)
          e.stopPropagation()
        }}
        title="Play chapter"
      >
        ▶
      </button>
    </div>
  )

  return (
    <div className="TutorialCurriculum">
      {relevantChapters?.length > 0 && (
        <div className="relevant-toggle-container">
          <RelevantToggle
            shouldOnlyShowRelevants={shouldOnlyShowRelevants}
            toggleShowingOnlyRelevantChapters={
              toggleShowingOnlyRelevantChapters
            }
          />
        </div>
      )}
      <div
        className={`TutorialCurriculum__content ${
          isExpanded || !shouldShowMore ? 'expanded' : 'collapsed'
        }`}
      >
        <section className="chapters">
          {chaptersToDisplay?.map((chapter, index) => (
            <Collapsible
              key={chapter.title}
              transitionTime={300}
              easing="ease-in-out"
              trigger={trigger(index, chapter)}
              open={isChapterNowPlaying(index)}
              onOpening={() => setIsExpanded(true)}
            >
              <ul className="chapter-markers">
                {Array.isArray(chapter.sections) &&
                  chapter?.sections?.map((marker) => {
                    const time = convertTime(marker.startTime)
                    return (
                      <li key={`${marker.title}-${time}`}>
                        <button
                          type="button"
                          className="marker"
                          aria-label={`Seek to ${marker.title} at ${time}`}
                        >
                          <span>
                            <a
                              href={`#t=${time}`}
                              onClick={() =>
                                playChapter(
                                  index + 1,
                                  chapter?.id,
                                  marker.startTime
                                )
                              }
                            >
                              {time}
                            </a>
                            &nbsp;- {marker.title}
                          </span>
                        </button>
                      </li>
                    )
                  })}
              </ul>
            </Collapsible>
          ))}
        </section>
      </div>
      {shouldShowMore && (
        <button
          onClick={toggleExpand}
          className="btn -blue-2 -thin"
          type="button"
        >
          {isExpanded ? 'Show Less' : 'Show More'}
        </button>
      )}
    </div>
  )
}

TutorialCurriculum.propTypes = TutorialPropTypes
TutorialCurriculum.defaultProps = TutorialDefaultProps
TutorialCurriculum.contextTypes = {
  router: PropTypes.object
}

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { getPlayerState } = stateProps
  const { videoId } = ownProps
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    ...getPlayerState(videoId)
  }
}

export default connect(
  stateSelector,
  dispatchers,
  mergeProps
)(withRouter(TutorialCurriculum))
